/**
 * NewsEditorPage.jsx — src/content/Manager/News/NewsEditorPage.jsx
 * Роут: /news/:id   (только head_manager / administrator)
 */
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, IconButton, Input,
    Option, Select, Sheet, Stack, Switch, Textarea, Tooltip, Typography,
} from '@mui/joy';
import { AnimatePresence, motion } from 'framer-motion';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import UnpublishedRoundedIcon from '@mui/icons-material/UnpublishedRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import CloudQueueRoundedIcon from '@mui/icons-material/CloudQueueRounded';

import { LayoutContext } from '../../../context';
import {
    APICreateNewsSlide, APIGetNewsAdminDetail, APIGetNewsAdminDictionaries,
    APIGetNewsAdminPreview, APIReorderNewsSlides, APISetNewsAdminStatus,
    APIUpdateNewsAdmin, APIUploadNewsCover,
} from '../../../api';
import NewsSlideEditor from '../../../components/News/NewsSlideEditor';
import NewsSlideContent from '../../../components/News/NewsSlideContent';

const SAVE_DELAY = 1500;

const cardSx = {
    borderRadius: '18px', border: '1px solid', borderColor: 'divider',
    p: 2.2, bgcolor: 'background.surface',
};

const labelSx = { fontSize: '0.72rem', fontWeight: 700, color: 'text.tertiary', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.4px' };


export default function NewsEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { tokenEmployee, notify, employeeAll, citys, setNewsStories } = useContext(LayoutContext);

    const [news, setNews] = useState(null);
    const [slides, setSlides] = useState([]);
    const [activeSlideId, setActiveSlideId] = useState(null);
    const [dicts, setDicts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);

    const pendingRef = useRef({});
    const timerRef = useRef(null);
    const coverInputRef = useRef(null);
    const dragIdRef = useRef(null);

    const activeSlide = slides.find(s => s.id === activeSlideId) || null;

    /* ── Загрузка ─────────────────────────────────────────────────────────── */
    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            const [detail, dictionaries] = await Promise.all([
                APIGetNewsAdminDetail(id, tokenEmployee),
                APIGetNewsAdminDictionaries(tokenEmployee),
            ]);
            if (!alive) return;

            if (detail?._error) {
                notify('Новость не найдена', 'danger');
                navigate('/news');
                return;
            }
            setNews(detail);
            const sorted = [...(detail.slides || [])].sort((a, b) => a.order - b.order);
            setSlides(sorted);
            setActiveSlideId(sorted[0]?.id || null);
            setDicts(dictionaries?._error ? null : dictionaries);
            setLoading(false);
        })();
        return () => { alive = false; };
    }, [id, tokenEmployee]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Автосохранение ───────────────────────────────────────────────────── */
    const flushSave = useCallback(async () => {
        const patch = pendingRef.current;
        pendingRef.current = {};
        if (!Object.keys(patch).length) return;

        setSaving(true);
        const res = await APIUpdateNewsAdmin(id, patch, tokenEmployee);
        setSaving(false);

        if (res?._error) {
            notify(res?.show_date?.[0] || res?.error || 'Не удалось сохранить', 'danger');
            return;
        }
        setDirty(false);
    }, [id, tokenEmployee, notify]);

    const patchNews = useCallback((patch) => {
        setNews(prev => ({ ...prev, ...patch }));
        pendingRef.current = { ...pendingRef.current, ...patch };
        setDirty(true);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flushSave, SAVE_DELAY);
    }, [flushSave]);

    // Предупреждение при уходе с несохранёнными правками
    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (!dirty) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            clearTimeout(timerRef.current);
        };
    }, [dirty]);

    /* ── Обложка ──────────────────────────────────────────────────────────── */
    const onCoverSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setBusy(true);
        const res = await APIUploadNewsCover(id, file, tokenEmployee);
        setBusy(false);
        if (res?._error) { notify(res?.error || 'Не удалось загрузить обложку', 'danger'); return; }
        setNews(prev => ({ ...prev, cover_url: res.cover_url }));
        notify('Обложка обновлена', 'success');
    };

    /* ── Слайды ───────────────────────────────────────────────────────────── */
    const addSlide = async () => {
        setBusy(true);
        const created = await APICreateNewsSlide(id, {
            layout: 'standard',
            title: `Слайд ${slides.length + 1}`,
            duration_seconds: 15,
            is_active: true,
        }, tokenEmployee);
        setBusy(false);
        if (created?._error) { notify(created?.error || 'Не удалось добавить слайд', 'danger'); return; }
        setSlides(prev => [...prev, created]);
        setActiveSlideId(created.id);
    };

    const onSlideUpdated = useCallback((updated) => {
        setSlides(prev => prev.map(s => (s.id === updated.id ? updated : s)));
    }, []);

    const onSlideDeleted = useCallback((slideId) => {
        setSlides(prev => {
            const next = prev.filter(s => s.id !== slideId);
            setActiveSlideId(cur => (cur === slideId ? (next[0]?.id || null) : cur));
            return next;
        });
    }, []);

    const persistOrder = async (list) => {
        const res = await APIReorderNewsSlides(id, list.map(s => s.id), tokenEmployee);
        if (res?._error) notify('Порядок слайдов не сохранился', 'danger');
    };

    const moveSlide = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= slides.length) return;
        const next = [...slides];
        [next[index], next[target]] = [next[target], next[index]];
        setSlides(next);
        persistOrder(next);
    };

    const onDrop = (targetId) => {
        const fromId = dragIdRef.current;
        dragIdRef.current = null;
        if (!fromId || fromId === targetId) return;
        const from = slides.findIndex(s => s.id === fromId);
        const to = slides.findIndex(s => s.id === targetId);
        if (from < 0 || to < 0) return;
        const next = [...slides];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setSlides(next);
        persistOrder(next);
    };

    /* ── Публикация и предпросмотр ────────────────────────────────────────── */
    const validateBeforePublish = () => {
        if (!news?.title?.trim()) return 'Укажите заголовок новости';
        if (!slides.some(s => s.is_active)) return 'Добавьте хотя бы один активный слайд';
        if (news.show_mode === 'exact_day' && !news.show_date) return 'Укажите дату показа';
        if (news.show_mode === 'range' && !news.show_date) return 'Укажите дату начала показа';
        return null;
    };

    const togglePublish = async () => {
        const next = news.status === 'published' ? 'draft' : 'published';
        if (next === 'published') {
            const error = validateBeforePublish();
            if (error) { notify(error, 'warning'); return; }
        }
        await flushSave();
        setBusy(true);
        const res = await APISetNewsAdminStatus(id, next, tokenEmployee);
        setBusy(false);
        if (res?._error) { notify(res?.error || 'Не удалось изменить статус', 'danger'); return; }
        setNews(prev => ({ ...prev, status: next }));
        notify(next === 'published' ? 'Новость опубликована' : 'Снята с публикации', 'success');
    };

    const openPreview = async () => {
        await flushSave();
        const data = await APIGetNewsAdminPreview(id, tokenEmployee);
        if (data?._error || !(data?.slides || []).length) {
            notify('Добавьте хотя бы один активный слайд', 'warning');
            return;
        }
        setNewsStories({ open: true, items: [data], preview: true });
    };

    if (loading || !news) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const roleOptions = dicts?.roles || [];
    const positionOptions = dicts?.positions || [];

    return (
        <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto', minWidth: 0 }}>
            <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onCoverSelected} />

            {/* Шапка */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box onClick={() => navigate('/news')} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                        color: 'text.tertiary', fontSize: '0.82rem', fontWeight: 600,
                        '&:hover': { color: '#6366f1' },
                    }}>
                        <ArrowBackRoundedIcon sx={{ fontSize: 16 }} /> Новости
                    </Box>
                    <Typography sx={{ color: 'text.tertiary', fontSize: '0.82rem' }}>/</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{news.title || 'Без названия'}</Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                        size="sm"
                        startDecorator={saving ? <CloudQueueRoundedIcon sx={{ fontSize: 14 }} /> : <CloudDoneRoundedIcon sx={{ fontSize: 14 }} />}
                        color={dirty || saving ? 'warning' : 'success'}
                        variant="soft"
                        sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                    >
                        {saving ? 'Сохраняем…' : dirty ? 'Есть изменения' : 'Сохранено'}
                    </Chip>
                    <Button variant="soft" color="neutral" startDecorator={<SaveRoundedIcon />} onClick={flushSave} disabled={!dirty}>
                        Сохранить
                    </Button>
                    <Button variant="soft" startDecorator={<VisibilityRoundedIcon />} onClick={openPreview}>
                        Предпросмотр
                    </Button>
                    <Button
                        color={news.status === 'published' ? 'warning' : 'success'}
                        startDecorator={news.status === 'published' ? <UnpublishedRoundedIcon /> : <PublishRoundedIcon />}
                        onClick={togglePublish}
                        loading={busy}
                    >
                        {news.status === 'published' ? 'Снять' : 'Опубликовать'}
                    </Button>
                </Stack>
            </Stack>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '340px minmax(0, 1fr) 300px' },
                gap: 2,
                alignItems: 'start',
            }}>
                {/* ── Колонка 1: настройки новости ── */}
                <Stack spacing={2}>
                    <Sheet sx={cardSx}>
                        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Основное</Typography>

                        <Typography sx={labelSx}>Заголовок</Typography>
                        <Input value={news.title || ''} onChange={(e) => patchNews({ title: e.target.value })} sx={{ mb: 1.4 }} />

                        <Typography sx={labelSx}>Подзаголовок</Typography>
                        <Input value={news.subtitle || ''} onChange={(e) => patchNews({ subtitle: e.target.value })} sx={{ mb: 1.4 }} />

                        <Typography sx={labelSx}>Внутренняя заметка</Typography>
                        <Textarea
                            minRows={2}
                            value={news.internal_note || ''}
                            onChange={(e) => patchNews({ internal_note: e.target.value })}
                            placeholder="Не видна пользователям"
                            sx={{ mb: 1.4 }}
                        />

                        <Typography sx={labelSx}>Обложка (для карточки)</Typography>
                        <Box
                            onClick={() => coverInputRef.current?.click()}
                            sx={{
                                height: 110, borderRadius: '14px', cursor: 'pointer',
                                border: '1px dashed', borderColor: 'divider',
                                background: news.cover_url ? `url(${news.cover_url}) center/cover no-repeat` : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                mb: 1.4, transition: 'all .2s', '&:hover': { borderColor: '#6366f1' },
                            }}
                        >
                            {!news.cover_url && (
                                <Stack alignItems="center" spacing={0.5}>
                                    <ImageRoundedIcon sx={{ color: 'text.tertiary' }} />
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Загрузить обложку</Typography>
                                </Stack>
                            )}
                        </Box>

                        <Typography sx={labelSx}>Акцентный цвет</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                                component="input"
                                type="color"
                                value={news.accent_color || '#6366f1'}
                                onChange={(e) => patchNews({ accent_color: e.target.value })}
                                sx={{ width: 46, height: 34, border: 'none', background: 'none', cursor: 'pointer', p: 0 }}
                            />
                            <Input
                                value={news.accent_color || ''}
                                onChange={(e) => patchNews({ accent_color: e.target.value })}
                                sx={{ flex: 1 }}
                            />
                        </Stack>
                    </Sheet>

                    <Sheet sx={cardSx}>
                        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Когда показывать</Typography>

                        <Typography sx={labelSx}>Режим показа</Typography>
                        <Select value={news.show_mode} onChange={(e, v) => v && patchNews({ show_mode: v })} sx={{ mb: 1.4 }}>
                            <Option value="exact_day">Только в указанный день</Option>
                            <Option value="range">В диапазоне дат</Option>
                            <Option value="always">Всегда, пока опубликована</Option>
                            <Option value="first_visit">При первом заходе (один раз)</Option>
                        </Select>

                        {news.show_mode !== 'always' && news.show_mode !== 'first_visit' && (
                            <>
                                <Typography sx={labelSx}>{news.show_mode === 'range' ? 'Дата начала' : 'Дата показа'}</Typography>
                                <Input
                                    type="date"
                                    value={news.show_date || ''}
                                    onChange={(e) => patchNews({ show_date: e.target.value || null })}
                                    sx={{ mb: 1.4 }}
                                />
                            </>
                        )}

                        {news.show_mode === 'range' && (
                            <>
                                <Typography sx={labelSx}>Дата окончания</Typography>
                                <Input
                                    type="date"
                                    value={news.show_date_end || ''}
                                    onChange={(e) => patchNews({ show_date_end: e.target.value || null })}
                                    sx={{ mb: 1.4 }}
                                />
                            </>
                        )}

                        <Typography sx={labelSx}>Приоритет в очереди</Typography>
                        <Input
                            type="number"
                            value={news.priority ?? 0}
                            onChange={(e) => patchNews({ priority: Number(e.target.value) || 0 })}
                            slotProps={{ input: { min: 0 } }}
                        />
                    </Sheet>

                    <Sheet sx={cardSx}>
                        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Кому показывать</Typography>

                        <Typography sx={labelSx}>Тип аудитории</Typography>
                        <Select value={news.audience_type} onChange={(e, v) => v && patchNews({ audience_type: v })} sx={{ mb: 1.4 }}>
                            <Option value="employees">Сотрудники</Option>
                            <Option value="clients">Клиенты</Option>
                            <Option value="all">Все</Option>
                        </Select>

                        <Typography sx={labelSx}>Роли (пусто — все)</Typography>
                        <Select
                            multiple
                            value={news.target_roles || []}
                            onChange={(e, v) => patchNews({ target_roles: v || [] })}
                            renderValue={(selected) => (
                                <Stack direction="row" spacing={0.4} sx={{ flexWrap: 'wrap' }}>
                                    {selected.map(o => <Chip key={o.value} size="sm" variant="soft">{o.label}</Chip>)}
                                </Stack>
                            )}
                            sx={{ mb: 1.4 }}
                        >
                            {roleOptions.map(r => <Option key={r.value} value={r.value}>{r.label}</Option>)}
                        </Select>

                        <Typography sx={labelSx}>Должности (пусто — все)</Typography>
                        <Select
                            multiple
                            value={news.target_positions || []}
                            onChange={(e, v) => patchNews({ target_positions: v || [] })}
                            renderValue={(selected) => (
                                <Stack direction="row" spacing={0.4} sx={{ flexWrap: 'wrap' }}>
                                    {selected.map(o => <Chip key={o.value} size="sm" variant="soft">{o.label}</Chip>)}
                                </Stack>
                            )}
                            sx={{ mb: 1.4 }}
                        >
                            {positionOptions.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
                        </Select>

                        <Typography sx={labelSx}>Города (пусто — все)</Typography>
                        <Autocomplete
                            multiple
                            placeholder="Выберите города"
                            options={citys || []}
                            getOptionLabel={(o) => o?.name || ''}
                            isOptionEqualToValue={(o, v) => o?.id === v?.id}
                            value={(citys || []).filter(c => (news.target_cities || []).includes(c.id))}
                            onChange={(e, v) => patchNews({ target_cities: (v || []).map(c => c.id) })}
                            sx={{ mb: 1.4 }}
                        />

                        <Typography sx={labelSx}>Конкретные сотрудники</Typography>
                        <Autocomplete
                            multiple
                            placeholder="Если выбрать — остальные условия игнорируются"
                            options={employeeAll || []}
                            getOptionLabel={(o) => `${o?.last_name || ''} ${o?.name || ''}`.trim() || `ID ${o?.id}`}
                            isOptionEqualToValue={(o, v) => o?.id === v?.id}
                            value={(employeeAll || []).filter(emp => (news.target_employees || []).includes(emp.id))}
                            onChange={(e, v) => patchNews({ target_employees: (v || []).map(emp => emp.id) })}
                            sx={{ mb: 1.4 }}
                        />

                        <Stack spacing={1}>
                            <Checkbox
                                size="sm"
                                label="Обязательная к просмотру"
                                checked={!!news.is_mandatory}
                                onChange={(e) => patchNews({ is_mandatory: e.target.checked })}
                            />
                            <Checkbox
                                size="sm"
                                label="Открывать сразу при входе (клиенту)"
                                checked={!!news.auto_open}
                                onChange={(e) => patchNews({ auto_open: e.target.checked })}
                            />
                            <Checkbox
                                size="sm"
                                label="Можно закрыть, не долистав"
                                checked={!!news.allow_skip}
                                onChange={(e) => patchNews({ allow_skip: e.target.checked })}
                            />
                            <Checkbox
                                size="sm"
                                label="Слать PUSH при публикации"
                                checked={news.push_on_publish !== false}
                                onChange={(e) => patchNews({ push_on_publish: e.target.checked })}
                            />
                        </Stack>
                    </Sheet>
                </Stack>

                {/* ── Колонка 2: слайды ── */}
                <Stack spacing={2} sx={{ minWidth: 0 }}>
                    <Sheet sx={{ ...cardSx, p: 1.6 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.2 }}>
                            <Typography sx={{ fontWeight: 800 }}>Слайды ({slides.length})</Typography>
                            <Button size="sm" startDecorator={<AddRoundedIcon />} onClick={addSlide} loading={busy}>
                                Добавить
                            </Button>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
                            {slides.map((slide, index) => (
                                <Box
                                    key={slide.id}
                                    draggable
                                    onDragStart={() => { dragIdRef.current = slide.id; }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => onDrop(slide.id)}
                                    onClick={() => setActiveSlideId(slide.id)}
                                    sx={{
                                        flex: '0 0 118px', height: 96, borderRadius: '12px',
                                        position: 'relative', cursor: 'pointer', overflow: 'hidden',
                                        border: '2px solid',
                                        borderColor: slide.id === activeSlideId ? '#6366f1' : 'divider',
                                        opacity: slide.is_active ? 1 : 0.5,
                                        background: slide.background_image_url
                                            ? `url(${slide.background_image_url}) center/cover no-repeat`
                                            : slide.images?.[0]?.url
                                                ? `url(${slide.images[0].url}) center/cover no-repeat`
                                                : (slide.background_color || '#f1f5f9'),
                                        transition: 'all .18s',
                                        '&:hover': { transform: 'translateY(-2px)' },
                                    }}
                                >
                                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent 65%)' }} />
                                    <DragIndicatorRoundedIcon sx={{ position: 'absolute', top: 4, left: 4, fontSize: 15, color: 'rgba(255,255,255,0.8)' }} />
                                    <Typography sx={{
                                        position: 'absolute', bottom: 6, left: 6, right: 6,
                                        color: '#fff', fontSize: '0.68rem', fontWeight: 700,
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {index + 1}. {slide.title || 'Без названия'}
                                    </Typography>

                                    <Stack direction="row" sx={{ position: 'absolute', top: 2, right: 2 }}>
                                        <IconButton size="sm" onClick={(e) => { e.stopPropagation(); moveSlide(index, -1); }} sx={{ minHeight: 20, minWidth: 20, color: '#fff' }}>
                                            <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14, transform: 'rotate(-90deg)' }} />
                                        </IconButton>
                                        <IconButton size="sm" onClick={(e) => { e.stopPropagation(); moveSlide(index, 1); }} sx={{ minHeight: 20, minWidth: 20, color: '#fff' }}>
                                            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14, transform: 'rotate(-90deg)' }} />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            ))}

                            {slides.length === 0 && (
                                <Box sx={{ p: 2, textAlign: 'center', width: '100%' }}>
                                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                        Слайдов пока нет — добавьте первый
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </Sheet>

                    <AnimatePresence mode="wait">
                        {activeSlide ? (
                            <motion.div
                                key={activeSlide.id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.18 }}
                            >
                                <NewsSlideEditor
                                    key={activeSlide.id}
                                    slide={activeSlide}
                                    token={tokenEmployee}
                                    notify={notify}
                                    onUpdated={onSlideUpdated}
                                    onDeleted={onSlideDeleted}
                                />
                            </motion.div>
                        ) : null}
                    </AnimatePresence>
                </Stack>

                {/* ── Колонка 3: живое превью ── */}
                <Sheet sx={{ ...cardSx, position: { lg: 'sticky' }, top: { lg: 16 } }}>
                    <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.4 }}>
                        <PhoneIphoneRoundedIcon sx={{ fontSize: 18, color: 'text.tertiary' }} />
                        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>Живое превью</Typography>
                    </Stack>

                    <Box sx={{
                        position: 'relative', width: '100%', maxWidth: 250, mx: 'auto',
                        aspectRatio: '9 / 16', borderRadius: '22px', overflow: 'hidden',
                        border: '6px solid #1a1a24', background: '#0d0d12',
                        boxShadow: '0 12px 34px rgba(15,23,42,0.25)',
                    }}>
                        {activeSlide ? (
                            <NewsSlideContent
                                slide={activeSlide}
                                expanded={false}
                                onToggleExpand={() => {}}
                                accentColor={news.accent_color || '#6366f1'}
                            />
                        ) : (
                            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                                <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.5)' }}>Нет слайда</Typography>
                            </Stack>
                        )}
                    </Box>

                    <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center', mt: 1.4 }}>
                        Так слайд увидят на телефоне. Кнопки и таймер — в полном предпросмотре.
                    </Typography>
                </Sheet>
            </Box>
        </Box>
    );
}